import React, { useState, useEffect, useRef } from 'react';
import {
  Printer,
  CreditCard,
  Clock,
  CalendarDays,
  ChevronRight,
  Settings as SettingsIcon,
  Building2,
  Phone,
  MapPin,
  Globe2,
  AtSign,
  User,
  Power,
  Layers,
  Edit,
  Users as UsersIcon,
  Utensils,
  RotateCcw,
  AlertTriangle,
  Link,
  SlidersHorizontal,
  Trash2,
  Plus,
  Save,
  RefreshCw,
} from 'lucide-react';
import PrinterManagement from './PrinterManagement';
import PaymentMethodsManagement from './PaymentMethodsManagement';
import ServiceHoursManagement from './ServiceHoursManagement';
import { showToast } from "../utils/toast";
import { extractBusinessType } from "../utils/businessType";
import { APP_VERSION } from "../version";
import { apiFetch } from "../utils/apiClient";
import StewardRoleSettings from "./StewardRoleSettings";

// --- Reset Manager (hoisted to avoid reference issues) ---
// (duplicate definition removed)

const Settings = ({ currentUser }) => {
  // Support des contextes Outlet/props + stockage séparé superadmin/admin
  const [storedAdminUser] = useState(() => {
    try {
      const raw = localStorage.getItem("moo_user");
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  });
  const [storedSuperUser] = useState(() => {
    try {
      const raw = localStorage.getItem("moo_user_superadmin");
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  });
  const isSuperPath =
    typeof window !== "undefined" && window.location.pathname.startsWith("/superadmin");

  const effectiveUser =
    currentUser ||
    (isSuperPath ? storedSuperUser || storedAdminUser : storedAdminUser || storedSuperUser);

  const derivedRole =
    effectiveUser?.role ||
    (isSuperPath && storedSuperUser ? "superadmin" : "admin");
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(() =>
    typeof document !== "undefined"
      ? document.documentElement.classList.contains("dark")
      : false,
  );
  const role = derivedRole === "superadmin" ? "superadmin" : "admin";

  useEffect(() => {
    const target = document.documentElement;
    const syncTheme = () => setIsDarkMode(target.classList.contains("dark"));
    syncTheme();
    const observer = new MutationObserver(syncTheme);
    observer.observe(target, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  const containerBg = isDarkMode
    ? "bg-gradient-to-br from-[#050b18] via-[#0f1c33] to-[#050b18] text-slate-100"
    : "bg-gradient-to-br from-slate-50 via-white to-slate-100 text-slate-900";
  const cardClasses = isDarkMode
    ? "bg-white/5 border border-white/10 shadow-2xl shadow-black/40"
    : "bg-white border border-slate-100 shadow-xl shadow-slate-200/60";
  const accentPill = isDarkMode
    ? "bg-amber-400/10 text-amber-200"
    : "bg-amber-100 text-amber-700";

  const settingsMenus = React.useMemo(() => ([
    {
      id: 'printers',
      title: 'Gestion des Imprimantes',
      description: 'Configurer les imprimantes de cuisine, bar et reçus',
      icon: Printer,
      component: PrinterManagement
    },
    {
      id: 'payment-methods',
      title: 'Moyens de Paiement',
      description: 'Gérer les moyens de paiement disponibles',
      icon: CreditCard,
      component: PaymentMethodsManagement
    },
    {
      id: 'service-hours',
      title: 'Horaires de Service',
      description: "Définir les plages horaires de fonctionnement de votre établissement",
      icon: Clock,
      component: ServiceHoursManagement
    },
    {
      id: 'restaurant-info',
      title: "Informations de l'établissement",
      description: "Coordonnées du restaurant, propriétaire et réseaux",
      icon: Building2,
      component: RestaurantInfoCard,
    },
    {
      id: 'packages',
      title: 'Packages',
      description: "Créer les formules d'abonnement et leurs accès",
      icon: Layers,
      component: PackagesManager,
    },
    {
      id: 'floor-plan',
      title: 'Plan de salles',
      description: 'Créer les aires et organiser les tables du restaurant',
      icon: MapPin,
      component: FloorPlanManager,
    },
    {
      id: 'steward-role',
      title: 'Rôle Steward',
      description: 'Organiser les tables par steward',
      icon: UsersIcon,
      component: StewardRoleSettings,
    },
    {
      id: 'admin-options',
      title: 'Options administrateur',
      description: "Autoriser certains droits sensibles pour le rôle admin",
      icon: SlidersHorizontal,
      component: AdminOptionsManager,
    },
    {
      id: 'reset',
      title: 'Remise a zero',
      description: 'Purger les donnees reelles (produits, extras, ventes, tables, utilisateurs, Waise)',
      icon: RotateCcw,
      component: ResetManager,
    },
    {
      id: 'cloud-link',
      title: 'Connexion Cloud',
      description: "Lier l'instance locale au cloud MOO Sync (API + token)",
      icon: Link,
      component: CloudLinkManager,
    },
    {
      id: "license-subscription",
      title: "Licences & Abonnement",
      description: "Définir l'expiration locale et suivre la priorité cloud",
      icon: CalendarDays,
      component: LicenseSubscriptionManager,
    },
  ]), []);

  const filteredMenus = React.useMemo(() => {
    if (role === "superadmin") return settingsMenus;
    // Admin : uniquement les horaires
    return settingsMenus.filter((menu) => menu.id === "service-hours");
  }, [role, settingsMenus]);

  useEffect(() => {
    if (activeSubmenu && !filteredMenus.find((m) => m.id === activeSubmenu)) {
      setActiveSubmenu(null);
    }
  }, [filteredMenus, activeSubmenu]);

  const handleMenuClick = (menuId) => {
    setActiveSubmenu(menuId);
  };

  const handleBackToMenu = () => {
    setActiveSubmenu(null);
  };

  // Si un sous-menu est actif, afficher le composant correspondant
  if (activeSubmenu) {
    const activeMenu = filteredMenus.find(menu => menu.id === activeSubmenu);
    const ComponentToRender = activeMenu?.component;

    return (
      <div className={`min-h-screen px-4 py-6 ${containerBg}`}>
        <div className={`${cardClasses} rounded-3xl px-6 py-4`}>
          <div className="flex items-center gap-3 text-sm">
            <button
              onClick={handleBackToMenu}
              className="inline-flex items-center gap-2 font-semibold text-amber-500 hover:text-amber-400 transition"
            >
              <SettingsIcon size={18} />
              Paramètres
            </button>
            <ChevronRight size={16} className="opacity-60" />
            <span className="font-semibold">
              {activeMenu?.title}
            </span>
          </div>
        </div>
        <div className="mt-6">
          {ComponentToRender && (
            <ComponentToRender
              cardClasses={cardClasses}
              isDarkMode={isDarkMode}
              onSaved={handleBackToMenu}
              role={role}
            />
          )}
        </div>
      </div>
    );
  }

  // Affichage du menu principal des paramètres
  return (
    <div className={`min-h-screen px-4 py-6 ${containerBg}`}>
        <div className="max-w-5xl mx-auto space-y-8">
        <div className="flex flex-col gap-2">
          <span className={`text-xs uppercase tracking-[0.4em] ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
            MOO Sync
          </span>
          <h1 className="text-3xl font-bold tracking-tight">Centre de configuration</h1>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {filteredMenus.length === 0 ? (
          <div className={`${cardClasses} rounded-3xl p-6 text-center text-sm ${isDarkMode ? "text-slate-200" : "text-slate-600"}`}>
            Aucun module disponible pour ce rôle.
          </div>
        ) : (
          filteredMenus.map((menu) => {
              const IconComponent = menu.icon;
              
              return (
                <button
                  key={menu.id}
                  onClick={() => handleMenuClick(menu.id)}
                  className={`${cardClasses} rounded-3xl p-6 text-left transition hover:-translate-y-1 hover:shadow-2xl`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow-lg`}>
                      <IconComponent size={22} />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold mb-1">{menu.title}</h3>
                      <p className={`text-sm ${isDarkMode ? "text-slate-300" : "text-slate-500"}`}>
                        {menu.description}
                      </p>
                    </div>
                    <ChevronRight className={`${isDarkMode ? "text-slate-400" : "text-slate-500"}`} size={18} />
                  </div>
                </button>
              );
            })
          )}
        </div>

        {role !== "superadmin" && (
          <div className={`${cardClasses} rounded-3xl p-6 border-amber-400/30`}>
            <div className="flex items-center gap-3 mb-2">
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${accentPill}`}>
                Accès limité
              </span>
              <h3 className="text-lg font-semibold">Paramètres réservés au superadmin</h3>
            </div>
            <p className={isDarkMode ? "text-slate-300 text-sm" : "text-slate-600 text-sm"}>
              La configuration des imprimantes, KDS, paiements et informations établissement est gérée par l'équipe Djizef (superadmin). Vous pouvez uniquement modifier les horaires de service.
            </p>
          </div>
        )}

        {role === "superadmin" && (
          <div className={`${cardClasses} rounded-3xl p-6`}>
            <div className="flex items-center gap-3 mb-4">
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${accentPill}`}>
                Système
              </span>
              <h3 className="text-lg font-semibold">Informations générales</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className={`${isDarkMode ? "bg-white/5" : "bg-slate-50"} rounded-2xl p-4`}>
                <div className={`text-sm ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>Version</div>
                <div className="text-xl font-semibold">MOO Sync v{APP_VERSION}</div>
              </div>
              <div className={`${isDarkMode ? "bg-white/5" : "bg-slate-50"} rounded-2xl p-4`}>
                <div className={`text-sm ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>Base de données</div>
                <div className="text-xl font-semibold">SQLite</div>
              </div>
              <div className={`${isDarkMode ? "bg-white/5" : "bg-slate-50"} rounded-2xl p-4`}>
                <div className={`text-sm ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>Statut</div>
                <div className="text-xl font-semibold text-green-500">Opérationnel</div>
              </div>
            </div>
          </div>
        )}
      </div>

    </div>
  );
};

export default Settings; 

const FloorPlanManager = ({ cardClasses, isDarkMode }) => {
  const [enabled, setEnabled] = useState(false);
  const [printAreaOnDocuments, setPrintAreaOnDocuments] = useState(false);
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newZoneName, setNewZoneName] = useState("");
  const [error, setError] = useState("");
  const operationInProgressRef = useRef(false);

  const request = async (url, options = {}) =>
    apiFetch(url, {
      includeBearer: true,
      tokenMode: "superadmin",
      credentials: "include",
      cache: "no-store",
      ...options,
    });

  const load = async ({ preserveError = false } = {}) => {
    setLoading(true);
    if (!preserveError) setError("");
    try {
      const [settingsResponse, zonesResponse] = await Promise.all([
        request(`/api/settings/floor-plan?_ts=${Date.now()}`),
        request(`/api/zones?includeInactive=1&_ts=${Date.now()}`),
      ]);
      if (!settingsResponse.ok || !zonesResponse.ok) {
        throw new Error("Impossible de charger le plan de salles.");
      }
      const settingsPayload = await settingsResponse.json();
      const zonesPayload = await zonesResponse.json();
      setEnabled(Boolean(settingsPayload?.floor_plan_enabled));
      setPrintAreaOnDocuments(Boolean(settingsPayload?.print_area_on_documents));
      setZones(Array.isArray(zonesPayload) ? zonesPayload : []);
    } catch (loadError) {
      setError(loadError?.message || "Erreur serveur");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const notifyUpdated = () => {
    try {
      localStorage.setItem("moo_floor_plan_enabled", String(enabled));
      localStorage.setItem("moo_floor_plan_broadcast", String(Date.now()));
    } catch (_error) {}
    window.dispatchEvent(new CustomEvent("moo-floor-plan-updated"));
  };

  const toggleEnabled = async () => {
    const next = !enabled;
    setSaving(true);
    setError("");
    try {
      const response = await request("/api/settings/floor-plan", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled: next }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || "Enregistrement impossible.");
      }
      const savedEnabled = Boolean(payload?.floor_plan_enabled);
      setEnabled(savedEnabled);
      try {
        localStorage.setItem("moo_floor_plan_enabled", String(savedEnabled));
        localStorage.setItem("moo_floor_plan_broadcast", String(Date.now()));
      } catch (_error) {}
      window.dispatchEvent(new CustomEvent("moo-floor-plan-updated"));
      showToast(
        "success",
        next ? "Plan de salles activé" : "Plan de salles désactivé",
      );
    } catch (saveError) {
      setError(saveError?.message || "Erreur serveur");
    } finally {
      setSaving(false);
    }
  };

  const togglePrintArea = async () => {
    const next = !printAreaOnDocuments;
    setSaving(true);
    setError("");
    try {
      const response = await request("/api/settings/floor-plan", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ print_area_on_documents: next }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error || "Enregistrement impossible.");
      setPrintAreaOnDocuments(Boolean(payload.print_area_on_documents));
      try {
        localStorage.setItem("moo_floor_plan_broadcast", String(Date.now()));
      } catch (_error) {}
      window.dispatchEvent(new CustomEvent("moo-floor-plan-updated"));
      showToast("success", next ? "Aire affichée sur les reçus et factures" : "Aire masquée sur les reçus et factures");
    } catch (saveError) {
      setError(saveError?.message || "Erreur serveur");
    } finally {
      setSaving(false);
    }
  };

  const saveZone = async (zone) => {
    const name = String(zone?.name || "").trim();
    if (!name || operationInProgressRef.current) return;
    operationInProgressRef.current = true;
    setSaving(true);
    setError("");
    try {
      const response = await request(`/api/zones/${zone.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          sort_order: Number(zone.sort_order) || 0,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error || "Modification impossible.");
      await load();
      notifyUpdated();
      showToast("success", "Aire mise à jour");
    } catch (saveError) {
      const message = saveError?.message || "Erreur serveur";
      await load({ preserveError: true }).catch(() => {});
      setError(message);
    } finally {
      operationInProgressRef.current = false;
      setSaving(false);
    }
  };

  const createZone = async () => {
    const name = newZoneName.trim();
    if (!name || operationInProgressRef.current) return;
    operationInProgressRef.current = true;
    setSaving(true);
    setError("");
    try {
      const response = await request("/api/zones", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, sort_order: zones.length }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error || "Création impossible.");
      setNewZoneName("");
      await load();
      notifyUpdated();
      showToast("success", "Aire ajoutée");
    } catch (saveError) {
      const message = saveError?.message || "Erreur serveur";
      await load({ preserveError: true }).catch(() => {});
      setError(message);
    } finally {
      operationInProgressRef.current = false;
      setSaving(false);
    }
  };

  const deleteZone = async (zone) => {
    if (Number(zone?.is_default) === 1) return;
    if (!window.confirm(`Supprimer l'aire "${zone.name}" ? Ses tables seront déplacées vers l'aire principale.`)) {
      return;
    }
    setSaving(true);
    setError("");
    try {
      const response = await request(`/api/zones/${zone.id}`, {
        method: "DELETE",
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error || "Suppression impossible.");
      await load();
      notifyUpdated();
      showToast("success", "Aire supprimée et tables réaffectées");
    } catch (saveError) {
      setError(saveError?.message || "Erreur serveur");
    } finally {
      setSaving(false);
    }
  };

  const inputClass = isDarkMode
    ? "border-white/10 bg-white/5 text-white"
    : "border-slate-200 bg-white text-slate-900";

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <div className={`${cardClasses} rounded-3xl p-6`}>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.35em] text-amber-400">
              Organisation du restaurant
            </p>
            <h2 className="mt-1 text-2xl font-semibold">Plan de salles</h2>
            <p className={isDarkMode ? "mt-2 text-sm text-slate-300" : "mt-2 text-sm text-slate-600"}>
              Regroupez les tables par rez-de-chaussée, étage, terrasse ou toute autre aire.
            </p>
          </div>
          <button
            type="button"
            disabled={loading || saving}
            onClick={toggleEnabled}
            className={`rounded-2xl px-5 py-3 text-sm font-semibold transition ${
              enabled
                ? "bg-emerald-500 text-white"
                : "bg-slate-500/30 text-slate-200"
            } disabled:opacity-50`}
          >
            {enabled ? "Activé" : "Désactivé"}
          </button>
        </div>
        <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-white/10 pt-5">
          <div>
            <p className="font-semibold">Afficher l’aire sur les reçus et factures</p>
            <p className="text-sm text-slate-400">Exemple : Aire : Terrasse. Désactivé par défaut.</p>
          </div>
          <button type="button" disabled={loading || saving || !enabled} onClick={togglePrintArea}
            className={`rounded-2xl px-5 py-3 text-sm font-semibold disabled:opacity-50 ${printAreaOnDocuments && enabled ? "bg-emerald-500 text-white" : "bg-slate-500/30 text-slate-200"}`}>
            {printAreaOnDocuments && enabled ? "Activé" : "Désactivé"}
          </button>
        </div>
        {error && (
          <div className="mt-4 rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-300">
            {error}
          </div>
        )}
      </div>

      <div className={`${cardClasses} rounded-3xl p-6`}>
        <div className="mb-4 flex items-center justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold">Aires</h3>
            <p className="text-sm text-slate-400">
              L'aire principale reste obligatoire. La numérotation des tables demeure globale.
            </p>
          </div>
          {loading && <RefreshCw className="animate-spin" size={18} />}
        </div>

        <div className="space-y-3">
          {zones.map((zone) => (
            <div
              key={zone.id}
              className="flex flex-col gap-3 rounded-2xl border border-white/10 p-4 sm:flex-row sm:items-center"
            >
              <input
                value={zone.name || ""}
                onChange={(event) =>
                  setZones((current) =>
                    current.map((item) =>
                      item.id === zone.id
                        ? { ...item, name: event.target.value }
                        : item,
                    ),
                  )
                }
                className={`min-w-0 flex-1 rounded-xl border px-3 py-2 ${inputClass}`}
              />
              <span className="text-xs text-slate-400">
                {Number(zone.table_count) || 0} table(s)
                {Number(zone.is_default) === 1 ? " · Par défaut" : ""}
              </span>
              <button
                type="button"
                disabled={saving}
                onClick={() => saveZone(zone)}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-amber-400 px-3 py-2 text-sm font-semibold text-slate-950 disabled:opacity-50"
              >
                <Save size={16} />
                Enregistrer
              </button>
              {Number(zone.is_default) !== 1 && (
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => deleteZone(zone)}
                  className="rounded-xl border border-rose-500/30 p-2 text-rose-300 hover:bg-rose-500/10 disabled:opacity-50"
                  title="Supprimer cette aire"
                >
                  <Trash2 size={17} />
                </button>
              )}
            </div>
          ))}
        </div>

        <div className="mt-5 flex flex-col gap-3 sm:flex-row">
          <input
            value={newZoneName}
            onChange={(event) => setNewZoneName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                void createZone();
              }
            }}
            placeholder="Nouvelle aire, ex. Terrasse"
            className={`flex-1 rounded-xl border px-3 py-2 ${inputClass}`}
          />
          <button
            type="button"
            disabled={saving || !newZoneName.trim()}
            onClick={createZone}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-500 px-4 py-2 font-semibold text-white disabled:opacity-50"
          >
            <Plus size={17} />
            Ajouter une aire
          </button>
        </div>
      </div>
    </div>
  );
};

const getAdminOptionsError = (res, payload, fallback) => {
  if (res.status === 401 || res.status === 403) {
    return "Session superadmin expirée. Reconnectez-vous.";
  }
  return payload?.error || fallback;
};

const AdminOptionsManager = ({ cardClasses, isDarkMode }) => {
  const [adminCanDeleteSales, setAdminCanDeleteSales] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;
    const loadOptions = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await apiFetch(`/api/superadmin/admin-options?_ts=${Date.now()}`, {
          includeBearer: true,
          tokenMode: "superadmin",
          headers: { "Cache-Control": "no-store" },
          credentials: "include",
          cache: "no-store",
        });
        if (!res.ok) {
          const payload = await res.json().catch(() => ({}));
          throw new Error(getAdminOptionsError(res, payload, "Lecture impossible"));
        }
        const payload = await res.json();
        if (mounted) {
          setAdminCanDeleteSales(Boolean(payload?.admin_can_delete_sales));
        }
      } catch (e) {
        if (mounted) setError(e?.message || "Erreur serveur");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    loadOptions();
    return () => {
      mounted = false;
    };
  }, []);

  const persistOption = async (nextValue) => {
    const previousValue = adminCanDeleteSales;
    setAdminCanDeleteSales(nextValue);
    setSaving(true);
    setError("");
    try {
      const res = await apiFetch("/api/superadmin/admin-options", {
        includeBearer: true,
        tokenMode: "superadmin",
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ admin_can_delete_sales: nextValue }),
      });
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(getAdminOptionsError(res, payload, "Enregistrement impossible"));
      }
      const payload = await res.json();
      setAdminCanDeleteSales(Boolean(payload?.admin_can_delete_sales));
      try {
        window.dispatchEvent(
          new CustomEvent("moo-admin-options-updated", {
            detail: {
              admin_can_delete_sales: Boolean(payload?.admin_can_delete_sales),
            },
          }),
        );
      } catch (_e) {
        // ignore
      }
      showToast("success", "Options administrateur enregistrées");
    } catch (e) {
      setAdminCanDeleteSales(previousValue);
      setError(e?.message || "Erreur serveur");
      showToast("error", "Impossible d'enregistrer l'option administrateur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-4">
      <div className={`${cardClasses} rounded-3xl p-6`}>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-amber-400">
              Droits admin
            </p>
            <h2 className="mt-1 text-2xl font-semibold">Options administrateur</h2>
            <p className={isDarkMode ? "mt-2 text-sm text-slate-300" : "mt-2 text-sm text-slate-600"}>
              Ces réglages donnent ponctuellement des droits sensibles au rôle admin.
            </p>
          </div>
          {loading && (
            <span className="rounded-full border border-white/10 px-3 py-1 text-xs text-slate-400">
              Chargement...
            </span>
          )}
        </div>

        <div className={`mt-6 rounded-2xl border p-4 ${
          isDarkMode ? "border-white/10 bg-white/5" : "border-slate-200 bg-slate-50"
        }`}>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="text-base font-semibold">
                Autoriser l'admin à supprimer une vente dans l'historique
              </h3>
              <p className={isDarkMode ? "mt-1 text-sm text-slate-300" : "mt-1 text-sm text-slate-600"}>
                La suppression reste un masquage des historiques et rapports. Les données de caisse,
                commandes et paiements restent conservées.
              </p>
            </div>
            <button
              type="button"
              disabled={loading || saving}
              onClick={() => persistOption(!adminCanDeleteSales)}
              className={`relative h-8 w-14 shrink-0 rounded-full transition ${
                adminCanDeleteSales ? "bg-emerald-500" : "bg-slate-500/50"
              } disabled:opacity-60`}
              aria-pressed={adminCanDeleteSales}
              aria-label="Autoriser la suppression des ventes par l'admin"
            >
              <span
                className={`absolute top-1 h-6 w-6 rounded-full bg-white shadow transition ${
                  adminCanDeleteSales ? "left-7" : "left-1"
                }`}
              />
            </button>
          </div>
        </div>

        {error && (
          <div className="mt-4 rounded-2xl border border-red-400/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
            {error}
          </div>
        )}
      </div>

    </div>
  );
};

const PackagesManager = ({ cardClasses, isDarkMode }) => {
  const [packages, setPackages] = useState([]);
  const [activePackageId, setActivePackageId] = useState("");
  const [restaurantInfo, setRestaurantInfo] = useState(null);
  const [menuSavingId, setMenuSavingId] = useState("");
  const [serverParticipationSavingId, setServerParticipationSavingId] =
    useState("");
  const [courseManagementSavingId, setCourseManagementSavingId] = useState("");
  const [fastFoodSavingId, setFastFoodSavingId] = useState("");
  const [menuModeModalPkg, setMenuModeModalPkg] = useState(null);
  const [menuModeChoice, setMenuModeChoice] = useState("catalog");
  const [menuModeSubmitting, setMenuModeSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingRolesPkg, setEditingRolesPkg] = useState(null);
  const [editingAllowedRolesPkg, setEditingAllowedRolesPkg] = useState(null);
  const [editingPagesPkg, setEditingPagesPkg] = useState(null);
  const [form, setForm] = useState({ name: "", pages: [], roleLimits: {} });
  const [saving, setSaving] = useState(false);
  const [roleLimitsDraft, setRoleLimitsDraft] = useState({});
  const [allowedRolesDraft, setAllowedRolesDraft] = useState({});
  const [pagesDraft, setPagesDraft] = useState([]);

  const AVAILABLE_PAGES = [
    { id: "dashboard", label: "Tableau de bord" },
    { id: "sales", label: "Ventes" },
    { id: "products", label: "Produits" },
    { id: "categories", label: "Catégories" },
    { id: "medias", label: "Médias" },
    { id: "users", label: "Utilisateurs" },
    { id: "permissions", label: "Permissions" },
    { id: "customers", label: "Clients" },
    { id: "tables", label: "Tables" },
    { id: "sessions", label: "Sessions" },
    { id: "waise", label: "Waise" },
    { id: "reports", label: "Rapports" },
    { id: "settings", label: "Paramètres" },
  ];
  const DEFAULT_PACKAGE_NAMES = new Set(["starter", "pro", "elite"]);
  const normalizePackageName = (value) =>
    String(value || "")
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
  const normalizePackagePages = (pages = []) =>
    Array.isArray(pages)
      ? Array.from(
          new Set(
            pages
              .map((page) => {
                const normalized = String(page || "").trim();
                return normalized === "keassa" ? "waise" : normalized;
              })
              .filter(Boolean),
          ),
        )
      : [];
  const normalizePackageList = (list = []) =>
    Array.isArray(list)
      ? list.map((pkg) => ({
          ...pkg,
          pages: normalizePackagePages(pkg?.pages),
          service_mode: (() => {
            const raw = String(pkg?.service_mode ?? pkg?.serviceMode ?? "")
              .trim()
              .toLowerCase();
            if (["restaurant", "fastfood", "hybrid"].includes(raw)) return raw;
            return pkg?.fast_food_enabled === true || pkg?.fastFoodEnabled === true
              ? "fastfood"
              : "restaurant";
          })(),
          fast_food_enabled:
            String(pkg?.service_mode ?? pkg?.serviceMode ?? "").toLowerCase() === "fastfood" ||
            (!pkg?.service_mode && !pkg?.serviceMode &&
              (pkg?.fast_food_enabled === true || pkg?.fastFoodEnabled === true)),
          course_management_enabled:
            pkg?.course_management_enabled === true ||
            pkg?.courseManagementEnabled === true,
        }))
      : [];

  const persist = async (list, activeId) => {
    const normalizedList = normalizePackageList(list);
    try {
      await apiFetch("/api/superadmin/packages", {
        includeBearer: true,
        tokenMode: "superadmin",
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ packages: normalizedList, activeId }),
      });
    } catch (e) {
      console.error("Persist packages failed", e);
    } finally {
      localStorage.setItem("moo_packages", JSON.stringify(normalizedList));
      localStorage.setItem("moo_active_package", activeId || "");
      localStorage.setItem("moo_package_broadcast", `${activeId}_${Date.now()}`);
      // L'événement storage ne se déclenche pas dans la fenêtre qui écrit.
      // La caisse relit donc la configuration autoritaire du serveur.
      window.dispatchEvent(new Event("moo-packages-updated"));
    }
  };

  // Charger les packages depuis le backend pour synchroniser entre navigateurs
  useEffect(() => {
    const loadPackages = async () => {
      const fallbackFromLocal = () => {
        try {
          const raw = localStorage.getItem("moo_packages");
          const activeId = localStorage.getItem("moo_active_package") || "";
          if (raw) {
            const parsed = normalizePackageList(JSON.parse(raw) || []);
            setPackages(parsed);
            setActivePackageId(activeId || parsed[0]?.id || "");
          }
        } catch (e) {
          console.warn("Lecture packages local échouée", e);
        }
      };

      try {
        const res = await apiFetch(`/api/superadmin/packages?_ts=${Date.now()}`, {
          includeBearer: true,
          tokenMode: "superadmin",
          headers: { "Cache-Control": "no-store" },
          credentials: "include",
          cache: "no-store",
        });
        if (res.ok && res.status !== 304) {
          const data = await res.json();
          const serverPkgs = normalizePackageList(data.packages);
          const activeId = data.activeId || serverPkgs[0]?.id || "";
          setPackages(serverPkgs);
          setActivePackageId(activeId);
          // Propager côté storage pour les autres vues/admin
          localStorage.setItem("moo_packages", JSON.stringify(serverPkgs));
          localStorage.setItem("moo_active_package", activeId);
          localStorage.setItem("moo_package_broadcast", `${activeId}_${Date.now()}`);
          return;
        }
      } catch (e) {
        console.warn("Chargement packages serveur échoué", e);
      }
      fallbackFromLocal();
    };

    loadPackages();
  }, []);

  useEffect(() => {
    const loadRestaurantInfo = async () => {
      try {
        const res = await fetch("/api/restaurant-info", { cache: "no-store" });
        if (res.ok) {
          const info = await res.json();
          setRestaurantInfo(info || {});
        }
      } catch (e) {
        console.warn("Chargement restaurant-info échoué", e);
      }
    };
    loadRestaurantInfo();
  }, []);

  const resolveMenuEnabled = (pkg) => {
    if (typeof pkg?.menu_enabled === "boolean") return pkg.menu_enabled;
    if (typeof pkg?.menuEnabled === "boolean") return pkg.menuEnabled;
    return false;
  };

  const resolveServerParticipationEnabled = (pkg) => {
    if (typeof pkg?.server_participation_enabled === "boolean") {
      return pkg.server_participation_enabled;
    }
    if (typeof pkg?.serverParticipationEnabled === "boolean") {
      return pkg.serverParticipationEnabled;
    }
    if (typeof pkg?.server_enabled === "boolean") {
      return pkg.server_enabled;
    }
    return false;
  };

  const resolveFastFoodEnabled = (pkg) => {
    if (typeof pkg?.fast_food_enabled === "boolean") {
      return pkg.fast_food_enabled;
    }
    if (typeof pkg?.fastFoodEnabled === "boolean") {
      return pkg.fastFoodEnabled;
    }
    return false;
  };

  const resolveCourseManagementEnabled = (pkg) =>
    pkg?.course_management_enabled === true ||
    pkg?.courseManagementEnabled === true;

  const resolveServiceMode = (pkg) => {
    const raw = String(pkg?.service_mode ?? pkg?.serviceMode ?? "")
      .trim()
      .toLowerCase();
    if (["restaurant", "fastfood", "hybrid"].includes(raw)) return raw;
    return resolveFastFoodEnabled(pkg) ? "fastfood" : "restaurant";
  };

  const resolveMenuMode = (pkg) => {
    const raw = String(pkg?.menu_mode ?? pkg?.menuMode ?? "catalog")
      .trim()
      .toLowerCase();
    return raw === "ordering" ? "ordering" : "catalog";
  };

  const togglePackageMenuEnabled = async (pkg) => {
    if (!pkg?.id || menuSavingId === pkg.id) return;
    const nextValue = !resolveMenuEnabled(pkg);
    if (!nextValue) {
      const updated = packages.map((p) =>
        p.id === pkg.id ? { ...p, menu_enabled: false } : p,
      );
      setPackages(updated);
      setMenuSavingId(pkg.id);
      try {
        await persist(updated, activePackageId);
        showToast("success", "Menu désactivé pour ce package");
      } finally {
        setMenuSavingId("");
      }
      return;
    }

    setMenuModeChoice(resolveMenuMode(pkg));
    setMenuModeModalPkg(pkg);
  };

  const confirmPackageMenuMode = async () => {
    if (!menuModeModalPkg?.id) return;
    const pkgId = menuModeModalPkg.id;
    const nextMode = menuModeChoice === "ordering" ? "ordering" : "catalog";
    const updated = packages.map((p) =>
      p.id === pkgId
        ? { ...p, menu_enabled: true, menu_mode: nextMode }
        : p,
    );
    setPackages(updated);
    setMenuModeSubmitting(true);
    setMenuSavingId(pkgId);
    try {
      await persist(updated, activePackageId);
      showToast(
        "success",
        nextMode === "ordering"
          ? "Menu activé en mode Commande"
          : "Menu activé en mode Catalogue",
      );
      setMenuModeModalPkg(null);
    } finally {
      setMenuModeSubmitting(false);
      setMenuSavingId("");
    }
  };

  const togglePackageServerParticipation = async (pkg) => {
    if (!pkg?.id || serverParticipationSavingId === pkg.id) return;
    const nextValue = !resolveServerParticipationEnabled(pkg);
    const updated = packages.map((p) =>
      p.id === pkg.id
        ? { ...p, server_participation_enabled: nextValue }
        : p,
    );
    setPackages(updated);
    setServerParticipationSavingId(pkg.id);
    try {
      await persist(updated, activePackageId);
      showToast(
        "success",
        nextValue
          ? "Participation des serveurs activée"
          : "Participation des serveurs désactivée",
      );
    } finally {
      setServerParticipationSavingId("");
    }
  };

  const togglePackageCourseManagement = async (pkg) => {
    if (!pkg?.id || courseManagementSavingId === pkg.id) return;
    const nextValue = !resolveCourseManagementEnabled(pkg);
    const updated = packages.map((entry) =>
      entry.id === pkg.id
        ? { ...entry, course_management_enabled: nextValue }
        : entry,
    );
    setPackages(updated);
    setCourseManagementSavingId(pkg.id);
    try {
      await persist(updated, activePackageId);
      showToast(
        "success",
        nextValue
          ? "Gestion des suites activée"
          : "Gestion des suites désactivée",
      );
    } finally {
      setCourseManagementSavingId("");
    }
  };

  const updatePackageServiceMode = async (pkg, nextMode) => {
    if (!pkg?.id || pkg.id !== activePackageId || fastFoodSavingId === pkg.id) return;
    if (!["restaurant", "fastfood", "hybrid"].includes(nextMode)) return;
    const updated = packages.map((p) =>
      p.id === pkg.id
        ? {
            ...p,
            service_mode: nextMode,
            fast_food_enabled: nextMode === "fastfood",
          }
        : p,
    );
    setPackages(updated);
    setFastFoodSavingId(pkg.id);
    try {
      await persist(updated, activePackageId);
      showToast(
        "success",
        nextMode === "hybrid"
          ? "Mode Mixte activé"
          : nextMode === "fastfood"
            ? "Mode Fast-food activé"
            : "Mode Restaurant activé",
      );
    } finally {
      setFastFoodSavingId("");
    }
  };

  const togglePage = (pageId) => {
    setForm((prev) => {
      const has = prev.pages.includes(pageId);
      return {
        ...prev,
        pages: has ? prev.pages.filter((p) => p !== pageId) : [...prev.pages, pageId],
      };
    });
  };

  const handleCreate = async () => {
    if (!form.name.trim() || form.pages.length === 0) {
      showToast("error", "Nom et accès obligatoires");
      return;
    }
    setSaving(true);
    try {
      const newPkg = {
        id: `pkg_${Date.now()}`,
        name: form.name.trim(),
        pages: form.pages,
        roleLimits: form.roleLimits || {},
        menu_enabled: restaurantInfo?.menu_enabled !== false,
        server_participation_enabled: false,
        course_management_enabled: false,
        fast_food_enabled: false,
        service_mode: "restaurant",
      };
      const next = [...packages, newPkg];
      setPackages(next);
      persist(next, activePackageId);
      setShowModal(false);
      setForm({ name: "", pages: [], roleLimits: {} });
      showToast("success", "Package créé");
    } finally {
      setSaving(false);
    }
  };

  const handleEditPagesSave = (pkgId, pages) => {
    if (!pages || pages.length === 0) {
      showToast("error", "Sélectionnez au moins une page");
      return;
    }
    const updated = packages.map((p) =>
      p.id === pkgId ? { ...p, pages: [...pages] } : p,
    );
    setPackages(updated);
    persist(updated, activePackageId);
    setEditingPagesPkg(null);
    setPagesDraft([]);
    showToast("success", "Pages mises à jour");
  };

  const handleActivate = (pkgId) => {
    setActivePackageId(pkgId);
    persist(packages, pkgId);
    showToast("success", "Package activé");
    // Notifier les autres onglets via localStorage pour recharger les menus
    try {
      localStorage.setItem("moo_package_broadcast", `${pkgId}_${Date.now()}`);
    } catch (e) {
      console.error("Broadcast package failed", e);
    }
  };

  const handleDeletePackage = (pkg) => {
    if (!pkg || pkg.id === activePackageId) {
      const fallback = packages.find((p) => p.id !== pkg?.id);
      if (!fallback) {
        showToast("error", "Impossible de supprimer le dernier package");
        return;
      }
    }
    if (!window.confirm(`Supprimer le package "${pkg.name}" ?`)) return;

    const next = packages.filter((p) => p.id !== pkg.id);
    const nextActive =
      pkg.id === activePackageId ? next[0]?.id || "" : activePackageId;
    setPackages(next);
    setActivePackageId(nextActive);
    persist(next, nextActive);
    showToast("success", "Package supprimé");
  };

  const isDefaultPackage = (pkg) =>
    Boolean(pkg?.isDefault) || DEFAULT_PACKAGE_NAMES.has(normalizePackageName(pkg?.name));

  const handleEditRolesSave = (pkgId, roleLimits) => {
    const updated = packages.map((p) =>
      p.id === pkgId ? { ...p, roleLimits: { ...roleLimits } } : p,
    );
    setPackages(updated);
    persist(updated, activePackageId);
    setEditingRolesPkg(null);
    setRoleLimitsDraft({});
    showToast("success", "Quotas mis à jour");
  };

  const handleAllowedRolesSave = (pkgId, rolesFlags) => {
    const updated = packages.map((p) =>
      p.id === pkgId ? { ...p, allowedRoles: { ...rolesFlags } } : p,
    );
    setPackages(updated);
    persist(updated, activePackageId);
    setEditingAllowedRolesPkg(null);
    setAllowedRolesDraft({});
    showToast("success", "Rôles autorisés mis à jour");
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className={`${cardClasses} rounded-3xl p-6 flex items-center justify-between`}>
        <div>
          <p className={`text-xs uppercase tracking-[0.3em] ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
            Abonnements
          </p>
          <h2 className="text-2xl font-semibold mt-1">Packages MOO Sync</h2>
          <p className={isDarkMode ? "text-slate-300 text-sm" : "text-slate-600 text-sm"}>
            Créez les formules et définissez les accès Admin.
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="rounded-2xl bg-gradient-to-r from-amber-400 to-orange-400 px-4 py-2 text-sm font-semibold text-slate-900 shadow hover:shadow-lg transition"
        >
          Ajouter un package
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {packages.length === 0 ? (
          <div className={`${cardClasses} rounded-3xl p-6 text-center text-sm ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>
            Aucun package créé. Cliquez sur "Ajouter un package".
          </div>
        ) : (
          packages.map((pkg) => {
            const isActive = activePackageId === pkg.id;
            return (
              <div key={pkg.id} className={`${cardClasses} rounded-3xl p-5 space-y-3`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-xs uppercase tracking-[0.25em] text-amber-300">Package</p>
                    <h3 className="text-lg font-semibold">{pkg.name}</h3>
                    <p className={isDarkMode ? "text-slate-300 text-sm" : "text-slate-600 text-sm"}>
                      {pkg.pages.length} page(s) autorisées
                    </p>
                    {pkg.roleLimits && Object.keys(pkg.roleLimits).length > 0 && (
                      <p className="text-xs mt-1 text-amber-200">
                        Quotas rôles définis
                      </p>
                    )}
                    {pkg.allowedRoles && Object.keys(pkg.allowedRoles).length > 0 && (
                      <p className="text-xs mt-1 text-emerald-200">
                        Rôles autorisés personnalisés
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        setEditingPagesPkg(pkg);
                        setPagesDraft(Array.isArray(pkg.pages) ? [...pkg.pages] : []);
                      }}
                      className="rounded-full p-2 border border-white/10 bg-white/5 text-white/80 hover:border-amber-300/60 transition"
                      title="Modifier les pages autorisées"
                    >
                      <SlidersHorizontal size={16} />
                    </button>
                    <button
                      onClick={() =>
                        setEditingRolesPkg({
                          ...pkg,
                          roleLimits: pkg.roleLimits || {},
                        })
                      }
                      className="rounded-full p-2 border border-white/10 bg-white/5 text-white/80 hover:border-amber-300/60 transition"
                      title="Définir les quotas de rôles"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      onClick={() =>
                        setEditingAllowedRolesPkg({
                          ...pkg,
                          allowedRoles: pkg.allowedRoles || {},
                        })
                      }
                      className="rounded-full p-2 border border-white/10 bg-white/5 text-white/80 hover:border-amber-300/60 transition"
                      title="Rôles autorisés"
                    >
                      <UsersIcon size={16} />
                    </button>
                    <button
                      onClick={() => handleActivate(pkg.id)}
                      className={`rounded-full p-2 border transition ${
                        isActive
                          ? "bg-emerald-500/20 border-emerald-400 text-emerald-300"
                          : "bg-white/5 border-white/10 text-white/70 hover:border-amber-300/60"
                      }`}
                      title={isActive ? "Package actif" : "Activer ce package"}
                    >
                      <Power size={18} />
                    </button>
                    {!isDefaultPackage(pkg) && (
                      <button
                        onClick={() => handleDeletePackage(pkg)}
                        className="rounded-full p-2 border border-red-500/40 bg-red-500/10 text-red-200 hover:border-red-400/70 transition"
                        title="Supprimer ce package"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 text-xs">
                  {pkg.pages.map((p) => {
                    const page = AVAILABLE_PAGES.find((x) => x.id === p);
                    return (
                      <span key={p} className="px-3 py-1 rounded-full border border-white/10 bg-white/5 text-white/80">
                        {page?.label || p}
                      </span>
                    );
                  })}
                </div>
                <div className="text-xs text-white/60">
                  Mode menu:{" "}
                  <span className="font-semibold text-white/80">
                    {resolveMenuMode(pkg) === "ordering" ? "Commande" : "Catalogue"}
                  </span>
                  {isActive && (
                    <>
                      <span className="mx-2 text-white/30">•</span>
                      <span className="font-semibold text-white/80">
                        Service : {resolveServiceMode(pkg) === "hybrid"
                          ? "Mixte"
                          : resolveServiceMode(pkg) === "fastfood"
                            ? "Fast-food"
                            : "Restaurant"}
                      </span>
                    </>
                  )}
                </div>
                <div className="flex justify-end">
                  <button
                    onClick={() => {
                      setMenuModeChoice(resolveMenuMode(pkg));
                      setMenuModeModalPkg(pkg);
                    }}
                    className="text-xs rounded-xl px-3 py-1 border border-white/10 bg-white/5 text-white/80 hover:border-amber-300/50 transition"
                    title="Configurer le mode du menu"
                  >
                    Configurer le mode
                  </button>
                </div>
                <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/10">
                  <button
                    onClick={() => togglePackageCourseManagement(pkg)}
                    disabled={courseManagementSavingId === pkg.id}
                    className={`rounded-full p-2 border transition ${
                      resolveCourseManagementEnabled(pkg)
                        ? "bg-emerald-500/20 border-emerald-400 text-emerald-300"
                        : "bg-red-500/15 border-red-400 text-red-300"
                    } ${
                      courseManagementSavingId === pkg.id
                        ? "opacity-60 cursor-not-allowed"
                        : "hover:border-amber-300/60"
                    }`}
                    title={
                      resolveCourseManagementEnabled(pkg)
                        ? "Désactiver la gestion des suites"
                        : "Activer la gestion des suites"
                    }
                    aria-label={
                      resolveCourseManagementEnabled(pkg)
                        ? "Désactiver la gestion des suites"
                        : "Activer la gestion des suites"
                    }
                  >
                    <Layers size={16} />
                  </button>
                  <button
                    onClick={() => togglePackageServerParticipation(pkg)}
                    disabled={serverParticipationSavingId === pkg.id}
                    className={`rounded-full p-2 border transition ${
                      resolveServerParticipationEnabled(pkg)
                        ? "bg-emerald-500/20 border-emerald-400 text-emerald-300"
                        : "bg-red-500/15 border-red-400 text-red-300"
                    } ${
                      serverParticipationSavingId === pkg.id
                        ? "opacity-60 cursor-not-allowed"
                        : "hover:border-amber-300/60"
                    }`}
                    title={
                      resolveServerParticipationEnabled(pkg)
                        ? "Désactiver la participation des serveurs (package)"
                        : "Activer la participation des serveurs (package)"
                    }
                    aria-label={
                      resolveServerParticipationEnabled(pkg)
                        ? "Désactiver la participation des serveurs (package)"
                        : "Activer la participation des serveurs (package)"
                    }
                  >
                    <UsersIcon size={16} />
                  </button>
                  <button
                    onClick={() => togglePackageMenuEnabled(pkg)}
                    disabled={menuSavingId === pkg.id}
                    className={`rounded-full p-2 border transition ${
                      resolveMenuEnabled(pkg)
                        ? "bg-emerald-500/20 border-emerald-400 text-emerald-300"
                        : "bg-red-500/15 border-red-400 text-red-300"
                    } ${menuSavingId === pkg.id ? "opacity-60 cursor-not-allowed" : "hover:border-amber-300/60"}`}
                    title={resolveMenuEnabled(pkg) ? "Désactiver le menu (package)" : "Activer le menu (package)"}
                    aria-label={resolveMenuEnabled(pkg) ? "Désactiver le menu (package)" : "Activer le menu (package)"}
                  >
                    <Utensils size={16} />
                  </button>
                  {isActive && (
                    <select
                      value={resolveServiceMode(pkg)}
                      onChange={(event) =>
                        updatePackageServiceMode(pkg, event.target.value)
                      }
                      disabled={fastFoodSavingId === pkg.id}
                      className="rounded-xl px-3 py-2 border border-white/15 bg-slate-900 text-white text-xs"
                      title="Mode de service du package actif"
                      aria-label="Mode de service"
                    >
                      <option value="restaurant">Restaurant</option>
                      <option value="fastfood">Fast-food</option>
                      <option value="hybrid">Mixte</option>
                    </select>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
          <div className={`${cardClasses} rounded-3xl p-6 w-full max-w-lg`}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-amber-300">Nouveau package</p>
                <h3 className="text-xl font-semibold">Définir l'accès admin</h3>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="rounded-full bg-white/10 text-white hover:bg-white/20 px-3 py-1 text-sm font-semibold"
              >
                Fermer
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-semibold block mb-1">Nom du package</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                  className="w-full rounded-2xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300/40 bg-white/5 border border-white/10 text-white placeholder-white/40"
                  placeholder="Ex: Standard, Premium..."
                />
              </div>
              <div>
                <label className="text-sm font-semibold block mb-2">Pages autorisées</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-60 overflow-y-auto pr-1">
                  {AVAILABLE_PAGES.map((page) => {
                    const checked = form.pages.includes(page.id);
                    return (
                      <label
                        key={page.id}
                        className={`flex items-center gap-2 rounded-xl px-3 py-2 text-sm border transition ${
                          checked
                            ? "border-amber-400/60 bg-amber-400/10 text-white"
                            : "border-white/10 bg-white/5 text-white/80 hover:border-amber-300/40"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => togglePage(page.id)}
                          className="accent-amber-400"
                        />
                        <span>{page.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="rounded-2xl px-4 py-2 text-sm font-semibold border border-white/10 text-white/80 hover:border-amber-300/50"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  disabled={saving}
                  onClick={handleCreate}
                  className="rounded-2xl bg-gradient-to-r from-amber-400 to-orange-400 px-4 py-2 text-sm font-semibold text-slate-900 disabled:opacity-50"
                >
                  {saving ? "Création..." : "Enregistrer"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {menuModeModalPkg && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
          <div className={`${cardClasses} rounded-3xl p-6 w-full max-w-md`}>
            <p className="text-xs uppercase tracking-[0.3em] text-amber-300">Activation menu</p>
            <h3 className="text-xl font-semibold mt-2">
              {resolveMenuEnabled(menuModeModalPkg)
                ? "Configurer le mode du menu"
                : "Choisir le mode du menu"}
            </h3>
            <p className={isDarkMode ? "text-slate-300 text-sm mt-2" : "text-slate-600 text-sm mt-2"}>
              Package: <span className="font-semibold">{menuModeModalPkg?.name}</span>
            </p>

            <div className="mt-5 grid grid-cols-1 gap-3">
              <button
                type="button"
                onClick={() => setMenuModeChoice("catalog")}
                className={`rounded-2xl border px-4 py-3 text-left transition ${
                  menuModeChoice === "catalog"
                    ? "border-amber-400/70 bg-amber-400/10"
                    : "border-white/10 bg-white/5 hover:border-amber-300/40"
                }`}
              >
                <div className="font-semibold">Catalogue</div>
                <div className="text-sm text-white/70">Le client consulte uniquement le menu.</div>
              </button>
              <button
                type="button"
                onClick={() => setMenuModeChoice("ordering")}
                className={`rounded-2xl border px-4 py-3 text-left transition ${
                  menuModeChoice === "ordering"
                    ? "border-emerald-400/70 bg-emerald-400/10"
                    : "border-white/10 bg-white/5 hover:border-emerald-300/40"
                }`}
              >
                <div className="font-semibold">Commande</div>
                <div className="text-sm text-white/70">Le client peut commander depuis le menu digital.</div>
              </button>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setMenuModeModalPkg(null)}
                disabled={menuModeSubmitting}
                className="rounded-2xl px-4 py-2 text-sm font-semibold border border-white/10 text-white/80 hover:border-amber-300/50 disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={confirmPackageMenuMode}
                disabled={menuModeSubmitting}
                className="rounded-2xl bg-gradient-to-r from-amber-400 to-orange-400 px-4 py-2 text-sm font-semibold text-slate-900 disabled:opacity-50"
              >
                {menuModeSubmitting
                  ? "Enregistrement..."
                  : resolveMenuEnabled(menuModeModalPkg)
                    ? "Enregistrer"
                    : "Activer"}
              </button>
            </div>
          </div>
        </div>
      )}

      {editingPagesPkg && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
          <div className={`${cardClasses} rounded-3xl p-6 w-full max-w-lg`}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-amber-300">Pages autorisées</p>
                <h3 className="text-xl font-semibold">Modifier l'accès du package</h3>
              </div>
              <button
                onClick={() => {
                  setEditingPagesPkg(null);
                  setPagesDraft([]);
                }}
                className="rounded-full bg-white/10 text-white hover:bg-white/20 px-3 py-1 text-sm font-semibold"
              >
                Fermer
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-semibold block mb-2">Pages autorisées</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-60 overflow-y-auto pr-1">
                  {AVAILABLE_PAGES.map((page) => {
                    const checked = pagesDraft.includes(page.id);
                    return (
                      <label
                        key={page.id}
                        className={`flex items-center gap-2 rounded-xl px-3 py-2 text-sm border transition ${
                          checked
                            ? "border-amber-400/60 bg-amber-400/10 text-white"
                            : "border-white/10 bg-white/5 text-white/80 hover:border-amber-300/40"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() =>
                            setPagesDraft((prev) =>
                              prev.includes(page.id)
                                ? prev.filter((p) => p !== page.id)
                                : [...prev, page.id],
                            )
                          }
                          className="accent-amber-400"
                        />
                        <span>{page.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setEditingPagesPkg(null);
                    setPagesDraft([]);
                  }}
                  className="rounded-2xl px-4 py-2 text-sm font-semibold border border-white/10 text-white/80 hover:border-amber-300/50"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={() => handleEditPagesSave(editingPagesPkg.id, pagesDraft)}
                  className="rounded-2xl bg-gradient-to-r from-amber-400 to-orange-400 px-4 py-2 text-sm font-semibold text-slate-900"
                >
                  Enregistrer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {editingAllowedRolesPkg && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
          <div className={`${cardClasses} rounded-3xl p-6 w-full max-w-xl`}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-amber-300">Rôles autorisés</p>
                <h3 className="text-xl font-semibold">Rôles disponibles pour l'admin</h3>
                <p className={isDarkMode ? "text-slate-300 text-sm" : "text-slate-600 text-sm"}>
                  Seuls les rôles activés seront proposés dans le modal "Nouvel utilisateur".
                </p>
              </div>
              <button
                onClick={() => {
                  setEditingAllowedRolesPkg(null);
                  setAllowedRolesDraft({});
                }}
                className="rounded-full bg-white/10 text-white hover:bg-white/20 px-3 py-1 text-sm font-semibold"
              >
                Fermer
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto pr-1">
              {[
                { id: "admin", label: "Administrateur" },
                { id: "manager", label: "Manager" },
                { id: "cashier", label: "Caissier" },
                { id: "server", label: "Serveur" },
                { id: "stockeur", label: "Stockeur" },
              ].map((role) => {
                const checked =
                  allowedRolesDraft[role.id] ??
                  editingAllowedRolesPkg.allowedRoles?.[role.id] ??
                  false;
                return (
                  <label
                    key={role.id}
                    className={`flex items-center gap-2 rounded-xl px-3 py-2 text-sm border transition ${
                      checked
                        ? "border-emerald-400/60 bg-emerald-400/10 text-white"
                        : "border-white/10 bg-white/5 text-white/80 hover:border-amber-300/40"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() =>
                        setAllowedRolesDraft((prev) => ({
                          ...prev,
                          [role.id]: !checked,
                        }))
                      }
                      className="accent-emerald-400"
                    />
                    <span>{role.label}</span>
                  </label>
                );
              })}
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button
                onClick={() => {
                  setEditingAllowedRolesPkg(null);
                  setAllowedRolesDraft({});
                }}
                className="rounded-2xl px-4 py-2 text-sm font-semibold border border-white/10 text-white/80 hover:border-amber-300/60"
              >
                Annuler
              </button>
              <button
                onClick={() => {
                  const merged = {
                    ...(editingAllowedRolesPkg.allowedRoles || {}),
                    ...allowedRolesDraft,
                  };
                  handleAllowedRolesSave(editingAllowedRolesPkg.id, merged);
                }}
                className="rounded-2xl bg-gradient-to-r from-amber-400 to-orange-400 px-4 py-2 text-sm font-semibold text-slate-900"
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {editingRolesPkg && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
          <div className={`${cardClasses} rounded-3xl p-6 w-full max-w-xl`}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-amber-300">Quotas de rôles</p>
                <h3 className="text-xl font-semibold">Limiter les comptes par rôle</h3>
                <p className={isDarkMode ? "text-slate-300 text-sm" : "text-slate-600 text-sm"}>
                  Ces limites s'appliqueront aux futurs ajouts de comptes dans admin/users.
                </p>
              </div>
              <button
                onClick={() => {
                  setEditingRolesPkg(null);
                  setRoleLimitsDraft({});
                }}
                className="rounded-full bg-white/10 text-white hover:bg-white/20 px-3 py-1 text-sm font-semibold"
              >
                Fermer
              </button>
            </div>

            <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
              {[
                { id: "admin", label: "Administrateur" },
                { id: "manager", label: "Manager" },
                { id: "cashier", label: "Caissier" },
                { id: "server", label: "Serveur" },
                { id: "stockeur", label: "Stockeur" },
              ].map((role) => {
                const value =
                  roleLimitsDraft[role.id] ??
                  editingRolesPkg.roleLimits?.[role.id] ??
                  "";
                return (
                  <div
                    key={role.id}
                    className={`rounded-2xl border px-4 py-3 ${
                      isDarkMode ? "border-white/10 bg-white/5" : "border-slate-200 bg-white"
                    } flex items-center justify-between gap-3`}
                  >
                    <span className="font-semibold">{role.label}</span>
                    <input
                      type="number"
                      min="0"
                      value={value}
                      onChange={(e) =>
                        setRoleLimitsDraft((prev) => ({
                          ...prev,
                          [role.id]: e.target.value,
                        }))
                      }
                      className="w-24 rounded-xl border border-white/10 bg-white/10 px-3 py-2 text-sm text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-amber-300/40"
                      placeholder="Illimité"
                    />
                  </div>
                );
              })}
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button
                onClick={() => {
                  setEditingRolesPkg(null);
                  setRoleLimitsDraft({});
                }}
                className="rounded-2xl px-4 py-2 text-sm font-semibold border border-white/10 text-white/80 hover:border-amber-300/60"
              >
                Annuler
              </button>
              <button
                onClick={() => {
                  const merged = { ...(editingRolesPkg.roleLimits || {}), ...roleLimitsDraft };
                  // Nettoyer valeurs vides
                  Object.keys(merged).forEach((k) => {
                    if (merged[k] === "" || merged[k] === null) delete merged[k];
                  });
                  handleEditRolesSave(editingRolesPkg.id, merged);
                }}
                className="rounded-2xl bg-gradient-to-r from-amber-400 to-orange-400 px-4 py-2 text-sm font-semibold text-slate-900"
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ResetManager = ({ cardClasses, isDarkMode }) => {
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [loadingKey, setLoadingKey] = useState("");

  const targets = [
    { id: "products", label: "Produits" },
    { id: "extras", label: "Extras seuls" },
    { id: "sales", label: "Ventes" },
    { id: "tables", label: "Tables" },
    { id: "users", label: "Utilisateurs (hors superadmin)" },
    { id: "waise", label: "Waise" },
    { id: "all", label: "Tout réinitialiser (ventes et rapports inclus)" },
  ];

  const triggerReset = (id) => setConfirmTarget({ env: "live", id });

  const confirmReset = async () => {
    if (!confirmTarget) return;
    const key = `${confirmTarget.id}`;
    setLoadingKey(key);
    try {
      const res = await apiFetch(`/api/superadmin/reset`, {
        includeBearer: true,
        tokenMode: "superadmin",
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ target: confirmTarget.id, env: "live" }),
        cache: "no-store",
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Erreur serveur");
      }
      showToast("success", confirmTarget.id === "products"
        ? "Catalogue vidé. Les anciennes ventes restent intactes."
        : confirmTarget.id === "extras"
          ? "Extras retirés du catalogue. Les anciennes ventes restent intactes."
          : "Données réinitialisées");
    } catch (e) {
      showToast("error", e.message || "Echec de la remise a zero");
    } finally {
      setLoadingKey("");
      setConfirmTarget(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className={`${cardClasses} rounded-3xl p-6`}>
        <div className="flex items-start gap-3">
          <div>
            <p className={`text-xs uppercase tracking-[0.3em] ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
              Maintenance LIVE
            </p>
            <h2 className="text-2xl font-semibold mt-1">Remise a zero (donnees reelles)</h2>
            <p className={`${isDarkMode ? "text-slate-300" : "text-slate-600"} text-sm`}>
              Purge la base LIVE de letablissement. Action definitive.
            </p>
          </div>
          <div className="ml-auto px-3 py-1 rounded-full text-xs font-semibold bg-red-500/20 text-red-200 border border-red-400/30 flex items-center gap-1">
            <AlertTriangle size={14} />
            Danger
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-5">
          {targets.map((t) => (
            <button
              key={t.id}
              onClick={() => triggerReset(t.id)}
              className={`flex items-center justify-between rounded-2xl px-4 py-3 border transition ${
                isDarkMode
                  ? "bg-white/5 border-white/10 hover:border-red-400/60"
                  : "bg-slate-50 border-slate-200 hover:border-red-400/60"
              }`}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                    isDarkMode ? "bg-red-500/15 text-red-200" : "bg-red-50 text-red-600"
                  }`}
                >
                  <RotateCcw size={18} />
                </div>
                <div className="text-left">
                  <p className="font-semibold">{t.label}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Reinitialiser</p>
                </div>
              </div>
              <ChevronRight size={16} className="text-slate-400" />
            </button>
          ))}
        </div>
      </div>

      {confirmTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
          <div className={`${cardClasses} rounded-3xl p-6 max-w-lg w-full`}>
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-red-500/15 text-red-200">
                <AlertTriangle />
              </div>
              <div className="space-y-1">
                <h3 className="text-xl font-semibold">Confirmer la remise a zero</h3>
                <p className={`${isDarkMode ? "text-slate-300" : "text-slate-600"} text-sm`}>
                  Vous allez purger les donnees "{targets.find((t) => t.id === confirmTarget.id)?.label}" sur
                  lenvironnement <strong>LIVE</strong>. Cette action est irreversible.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setConfirmTarget(null)}
                className="rounded-2xl px-4 py-2 text-sm font-semibold border border-white/15 text-white hover:border-amber-300/60"
              >
                Annuler
              </button>
              <button
                disabled={loadingKey === `${confirmTarget.id}`}
                onClick={confirmReset}
                className="rounded-2xl px-4 py-2 text-sm font-semibold bg-gradient-to-r from-amber-400 to-orange-400 text-slate-900 shadow disabled:opacity-60"
              >
                {loadingKey === `${confirmTarget.id}` ? "Nettoyage..." : "Confirmer"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
// --- Cloud link (local -> cloud) ---
const DEFAULT_CLOUD_API_URL = "https://moosync.com/api/cloud";

const LicenseSubscriptionManager = ({ cardClasses, isDarkMode }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [state, setState] = useState({
    local_policy: null,
    cloud_snapshot: null,
    effective_state: null,
    cloud_linked: false,
  });
  const [formDate, setFormDate] = useState("");
  const [note, setNote] = useState("");

  const toDateInput = (value) => {
    const iso = String(value || "").trim();
    if (!iso) return "";
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return "";
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const toHumanDateTime = (value) => {
    const iso = String(value || "").trim();
    if (!iso) return "—";
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return "—";
    return date.toLocaleString("fr-FR");
  };

  const loadState = React.useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiFetch("/api/superadmin/license", {
        includeBearer: true,
        tokenMode: "superadmin",
        credentials: "include",
        cache: "no-store",
      });
      if (!res.ok) throw new Error("Erreur lecture licence");
      const data = await res.json();
      setState({
        local_policy: data?.local_policy || null,
        cloud_snapshot: data?.cloud_snapshot || null,
        effective_state: data?.effective_state || null,
        cloud_linked: Boolean(data?.cloud_linked),
      });
      setFormDate(toDateInput(data?.local_policy?.expires_at || data?.effective_state?.effective_expires_at));
    } catch (error) {
      showToast("error", error.message || "Erreur chargement licence");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadState();
  }, [loadState]);

  const saveWithPayload = async (payload) => {
    setSaving(true);
    try {
      const res = await apiFetch("/api/superadmin/license", {
        includeBearer: true,
        tokenMode: "superadmin",
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.error || "Erreur mise à jour licence");
      }
      showToast("success", "Licence locale mise à jour");
      await loadState();
      setNote("");
    } catch (error) {
      showToast("error", error.message || "Erreur mise à jour licence");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveByDate = async () => {
    if (!formDate) {
      showToast("error", "Sélectionnez une date d'expiration");
      return;
    }
    const expiresAtIso = new Date(`${formDate}T23:59:59`).toISOString();
    await saveWithPayload({
      expires_at: expiresAtIso,
      note: note.trim() || null,
    });
  };

  const handleQuickPeriod = async (periodDays) => {
    await saveWithPayload({
      period_days: periodDays,
      note: note.trim() || null,
    });
  };

  const effective = state?.effective_state || {};
  const cloud = state?.cloud_snapshot || {};
  const isBlocked = Boolean(effective?.blocked);
  const reason = String(effective?.reason || "ok");
  const reasonLabelMap = {
    ok: "Actif",
    cloud_suspended: "Suspendu par le cloud",
    local_expired: "Expiré (date locale)",
    cloud_expired: "Expiré (date cloud)",
  };
  const effectiveLabel = reasonLabelMap[reason] || reason;

  return (
    <div className="max-w-5xl mx-auto space-y-4">
      <div className={`${cardClasses} rounded-3xl p-6`}>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-amber-400">Licence</p>
            <h2 className="text-2xl font-semibold mt-1">Licences & Abonnement</h2>
            <p className={isDarkMode ? "text-slate-300 text-sm" : "text-slate-600 text-sm"}>
              Définissez l'expiration locale. En instance liée, la suspension cloud reste prioritaire.
            </p>
          </div>
          <div
            className={`px-3 py-1 rounded-full text-xs font-semibold border ${
              isBlocked
                ? "bg-red-500/15 text-red-200 border-red-400/40"
                : "bg-emerald-500/15 text-emerald-200 border-emerald-400/40"
            }`}
          >
            {effectiveLabel}
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-semibold">Date d'expiration locale</label>
            <input
              type="date"
              value={formDate}
              onChange={(e) => setFormDate(e.target.value)}
              className="w-full rounded-2xl px-4 py-3 border border-white/15 bg-white/5 text-white focus:outline-none focus:ring-2 focus:ring-amber-300/40"
            />
            <p className="text-xs text-slate-400">
              Expiration locale courante: {toHumanDateTime(state?.local_policy?.expires_at)}
            </p>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-semibold">Note interne (optionnel)</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              className="w-full rounded-2xl px-4 py-3 border border-white/15 bg-white/5 text-white focus:outline-none focus:ring-2 focus:ring-amber-300/40 resize-none"
              placeholder="Ex: Renouvellement client"
            />
          </div>
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          {[30, 60, 90].map((days) => (
            <button
              key={days}
              type="button"
              disabled={saving}
              onClick={() => handleQuickPeriod(days)}
              className="rounded-2xl px-4 py-2 text-sm font-semibold border border-white/15 text-white/90 hover:border-amber-300/60 disabled:opacity-60"
            >
              {days} jours
            </button>
          ))}
          <button
            type="button"
            disabled={saving || !formDate}
            onClick={handleSaveByDate}
            className="rounded-2xl bg-gradient-to-r from-amber-400 to-orange-400 px-4 py-2 text-sm font-semibold text-slate-900 disabled:opacity-60"
          >
            {saving ? "Enregistrement..." : "Enregistrer la date"}
          </button>
        </div>
      </div>

      <div className={`${cardClasses} rounded-3xl p-6`}>
        <h3 className="text-lg font-semibold">État effectif appliqué</h3>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
          <div className={`${isDarkMode ? "bg-white/5" : "bg-slate-50"} rounded-2xl p-4`}>
            <div className="text-xs text-slate-400">Source cloud liée</div>
            <div className="font-semibold">{state.cloud_linked ? "Oui" : "Non"}</div>
          </div>
          <div className={`${isDarkMode ? "bg-white/5" : "bg-slate-50"} rounded-2xl p-4`}>
            <div className="text-xs text-slate-400">Expiration effective</div>
            <div className="font-semibold">{toHumanDateTime(effective?.effective_expires_at)}</div>
          </div>
          <div className={`${isDarkMode ? "bg-white/5" : "bg-slate-50"} rounded-2xl p-4`}>
            <div className="text-xs text-slate-400">Dernier calcul</div>
            <div className="font-semibold">{toHumanDateTime(effective?.computed_at)}</div>
          </div>
        </div>

        <div className="mt-5 rounded-2xl border border-white/10 bg-white/5 p-4 text-sm">
          <p className="font-semibold mb-2">Snapshot licence cloud</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <div className="text-xs text-slate-400">Statut cloud</div>
              <div>{String(cloud?.status || "—")}</div>
            </div>
            <div>
              <div className="text-xs text-slate-400">Expiration cloud</div>
              <div>{toHumanDateTime(cloud?.expires_at)}</div>
            </div>
            <div>
              <div className="text-xs text-slate-400">Révision cloud</div>
              <div>{Number.isFinite(Number(cloud?.revision)) ? Number(cloud.revision) : "—"}</div>
            </div>
          </div>
        </div>

        <div className="mt-5 flex justify-end">
          <button
            type="button"
            disabled={loading}
            onClick={loadState}
            className="rounded-2xl px-4 py-2 text-sm font-semibold border border-white/15 text-white/90 hover:border-amber-300/60 disabled:opacity-60"
          >
            {loading ? "Chargement..." : "Rafraîchir"}
          </button>
        </div>
      </div>

    </div>
  );
};

const CloudLinkManager = ({ cardClasses, isDarkMode }) => {
  const [link, setLink] = useState(null);
  const [runtimeStatus, setRuntimeStatus] = useState(null);
  const [form, setForm] = useState({
    api_url: DEFAULT_CLOUD_API_URL,
    activation_token: "",
  });
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const loadRuntimeStatus = async () => {
    try {
      const res = await apiFetch("/api/status", {
        includeBearer: true,
        tokenMode: "superadmin",
        credentials: "include",
        cache: "no-store",
      });
      if (!res.ok) return;
      const data = await res.json().catch(() => null);
      if (!data || typeof data !== "object") return;
      setRuntimeStatus({
        cloudLinkConfigured: Boolean(data.cloudLinkConfigured),
        cloudLink: Boolean(data.cloudLink),
        pendingSyncCount: Number(data.pendingSyncCount || 0),
        lastCloudSyncAt: data.lastCloudSyncAt || null,
      });
    } catch (_e) {
      // ignore
    }
  };

  const load = async () => {
    try {
      setLoading(true);
      const res = await apiFetch("/api/superadmin/cloud-link", {
        includeBearer: true,
        tokenMode: "superadmin",
        credentials: "include",
        cache: "no-store",
      });
      if (res.ok) {
        const data = await res.json();
        setLink(data);
        if (data) {
          setForm({
            api_url: data.api_url || DEFAULT_CLOUD_API_URL,
            activation_token: (data.activation_token || "").trim(),
          });
        }
      }
      await loadRuntimeStatus();
    } catch (e) {
      console.warn("Cloud link load failed", e);
    } finally {
      setLoading(false);
    }
  };

  const stopLink = async () => {
    setDisconnecting(true);
    try {
      const res = await apiFetch("/api/superadmin/cloud-link", {
        includeBearer: true,
        tokenMode: "superadmin",
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Arrêt impossible");
      }
      setLink(null);
      setForm({ api_url: DEFAULT_CLOUD_API_URL, activation_token: "" });
      await loadRuntimeStatus();
      showToast("success", "Connexion cloud arrêtée");
    } catch (e) {
      showToast("error", e.message || "Erreur lors de l'arrêt");
    } finally {
      setDisconnecting(false);
    }
  };

  useEffect(() => {
    load();
    loadRuntimeStatus();
  }, []);

  const save = async () => {
    let apiUrl = (form.api_url || DEFAULT_CLOUD_API_URL).trim();
    if (!apiUrl || !form.activation_token.trim()) {
      showToast("error", "URL et token requis");
      return;
    }
    if (apiUrl.match(/\/cloud\/?$/) && !apiUrl.includes("/api/cloud")) {
      apiUrl = apiUrl.replace(/\/cloud\/?$/, "/api/cloud");
    }
    apiUrl = apiUrl.replace(/\/+$/, "");
    setLoading(true);
    try {
      const res = await apiFetch("/api/superadmin/cloud-link", {
        includeBearer: true,
        tokenMode: "superadmin",
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          api_url: apiUrl,
          activation_token: form.activation_token.trim(),
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Erreur de sauvegarde");
      }
      await load();
      await loadRuntimeStatus();
      showToast("success", "Lien cloud enregistré");
    } catch (e) {
      showToast("error", e.message);
    } finally {
      setLoading(false);
    }
  };

  const testLink = async () => {
    setTesting(true);
    try {
      let apiUrl = (form.api_url || DEFAULT_CLOUD_API_URL).trim();
      if (apiUrl.match(/\/cloud\/?$/) && !apiUrl.includes("/api/cloud")) {
        apiUrl = apiUrl.replace(/\/cloud\/?$/, "/api/cloud");
      }
      apiUrl = apiUrl.replace(/\/+$/, "");
      const res = await apiFetch("/api/superadmin/cloud-link/test", {
        includeBearer: true,
        tokenMode: "superadmin",
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ ...form, api_url: apiUrl }),
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok && body.ok !== false) {
        showToast("success", "Connexion cloud OK");
      } else {
        showToast("error", body.error || body.details || "Test KO");
      }
      await load();
      await loadRuntimeStatus();
    } catch (e) {
      showToast("error", e.message || "Test KO");
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className={`max-w-4xl mx-auto ${cardClasses} rounded-3xl p-6`}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-amber-400">Cloud</p>
          <h2 className="text-xl font-semibold">Connexion au cloud MOO Sync</h2>
          <p className={isDarkMode ? "text-slate-300 text-sm" : "text-slate-600 text-sm"}>
            Déclare l'URL du serveur cloud et le token d'activation pour synchroniser cette instance locale.
          </p>
        </div>
        <span
          className={`px-3 py-1 rounded-full text-xs font-semibold ${
            link?.last_status === "ok"
              ? "bg-emerald-500/15 text-emerald-300 border border-emerald-400/40"
              : "bg-white/10 text-white border border-white/20"
          }`}
        >
          {link?.last_status === "ok" ? "Connecté" : link?.last_status || "Inconnu"}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="text-sm font-semibold">URL API cloud</label>
          <input
            value={form.api_url}
            onChange={(e) =>
              setForm((p) => ({
                ...p,
                api_url: e.target.value || DEFAULT_CLOUD_API_URL,
              }))
            }
            className="w-full rounded-2xl px-4 py-3 border border-white/15 bg-white/5 text-white focus:outline-none focus:ring-2 focus:ring-amber-300/40"
            placeholder="https://moosync.com/api/cloud"
            disabled
          />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-semibold">Token d'activation</label>
          <input
            value={form.activation_token}
            onChange={(e) => setForm((p) => ({ ...p, activation_token: e.target.value }))}
            className="w-full rounded-2xl px-4 py-3 border border-white/15 bg-white/5 text-white focus:outline-none focus:ring-2 focus:ring-amber-300/40"
            placeholder="ACT-XXXXXX"
          />
          {link?.activation_token && (
            <p className="text-xs text-slate-400">
              Token enregistré : <span className="font-semibold text-white">{(link.activation_token || "").trim()}</span>
            </p>
          )}
        </div>
      </div>

      {link?.last_error && (
        <div className="mt-3 text-sm text-red-300 bg-red-500/10 border border-red-500/40 rounded-2xl px-4 py-2">
          Dernière erreur: {link.last_error}
        </div>
      )}

      <div className="mt-5 flex flex-wrap gap-3">
        <button
          onClick={save}
          disabled={loading}
          className="rounded-2xl px-4 py-2 text-sm font-semibold bg-gradient-to-r from-amber-400 to-orange-400 text-slate-900 disabled:opacity-60"
        >
          {loading ? "Enregistrement..." : "Enregistrer"}
        </button>
        <button
          onClick={testLink}
          disabled={testing}
          className="rounded-2xl px-4 py-2 text-sm font-semibold border border-white/15 text-white hover:border-amber-300/60 disabled:opacity-60"
        >
          {testing ? "Test en cours..." : "Tester la connexion"}
        </button>
        <button
          onClick={stopLink}
          disabled={disconnecting}
          className="rounded-2xl px-4 py-2 text-sm font-semibold border border-red-400/40 text-red-300 hover:border-red-300/60 disabled:opacity-60"
        >
          {disconnecting ? "Arrêt..." : "Arrêter la connexion"}
        </button>
        <div className={isDarkMode ? "text-slate-400 text-sm" : "text-slate-600 text-sm"}>
          Dernier check: {link?.last_checked ? new Date(link.last_checked).toLocaleString() : "—"}
        </div>
      </div>

      <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-4 text-sm">
        <p className="font-semibold mb-2">Etat runtime cloud</p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <div className="text-xs text-slate-400">Cloud configure</div>
            <div>{runtimeStatus?.cloudLinkConfigured ? "Oui" : "Non"}</div>
          </div>
          <div>
            <div className="text-xs text-slate-400">Installation connectee au cloud</div>
            <div>{runtimeStatus?.cloudLink ? "Oui" : "Non"}</div>
          </div>
          <div>
            <div className="text-xs text-slate-400">Sync en attente</div>
            <div>{Number(runtimeStatus?.pendingSyncCount || 0)}</div>
          </div>
          <div>
            <div className="text-xs text-slate-400">Derniere sync</div>
            <div>
              {runtimeStatus?.lastCloudSyncAt
                ? new Date(runtimeStatus.lastCloudSyncAt).toLocaleString()
                : "-"}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const RestaurantInfoCard = ({ cardClasses, isDarkMode, keepMounted = false, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingCovers, setUploadingCovers] = useState(false);
  const [initial, setInitial] = useState({
    name: "",
    phone: "",
    address: "",
    email: "",
    website: "",
    instagram: "",
    facebook: "",
    tiktok: "",
    owner_name: "",
    owner_phone: "",
    logo_path: "",
    menu_covers: [],
    menu_descriptions: [],
  });
  const [logoPath, setLogoPath] = useState("");
  const [menuCovers, setMenuCovers] = useState([]);
  const [menuDescriptions, setMenuDescriptions] = useState([]);
  const [businessType, setBusinessType] = useState("restaurant");

  // Refs pour éviter les rerenders à chaque frappe
  const refs = React.useRef({
    name: null,
    phone: null,
    address: null,
    email: null,
    website: null,
    instagram: null,
    facebook: null,
    tiktok: null,
    owner_name: null,
    owner_phone: null,
  });

  const uploadRestaurantImage = async (endpoint, file) => {
    const formData = new FormData();
    formData.append("image", file);
    const res = await fetch(endpoint, { method: "POST", body: formData });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body.error || "Upload impossible");
    if (!body?.path) throw new Error("Réponse invalide (chemin manquant)");
    return body.path;
  };

  const handleUploadLogo = async (file) => {
    if (!file) return;
    setUploadingLogo(true);
    try {
      const path = await uploadRestaurantImage("/api/upload/restaurant-logo", file);
      setLogoPath(path);
      showToast("success", "Logo mis à jour");
    } catch (e) {
      showToast("error", e.message || "Upload logo impossible");
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleUploadCovers = async (files) => {
    const list = Array.from(files || []);
    if (list.length === 0) return;

    const remaining = Math.max(0, 3 - (menuCovers?.length || 0));
    if (remaining <= 0) {
      showToast("info", "Maximum 3 covers");
      return;
    }

    setUploadingCovers(true);
    try {
      const toUpload = list.slice(0, remaining);
      const uploaded = [];
      for (const file of toUpload) {
        // eslint-disable-next-line no-await-in-loop
        const path = await uploadRestaurantImage("/api/upload/restaurant-cover", file);
        uploaded.push(path);
      }
      setMenuCovers((prev) => [...(prev || []), ...uploaded].slice(0, 3));
      showToast("success", "Cover(s) ajoutée(s)");
    } catch (e) {
      showToast("error", e.message || "Upload cover impossible");
    } finally {
      setUploadingCovers(false);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(refs.current).map(([key, ref]) => [key, ref?.value || ""]),
      );
      payload.logo_path = logoPath || "";
      payload.menu_covers = Array.isArray(menuCovers) ? menuCovers.slice(0, 3) : [];
      payload.menu_descriptions = Array.isArray(menuDescriptions)
        ? menuDescriptions
            .map((t) => String(t || "").trim())
            .filter(Boolean)
            .slice(0, 4)
        : [];
      payload.business_type = businessType;

      // Sauvegarde API locale (offline-friendly)
      await fetch("/api/restaurant-info", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      // Cache local pour fallback éventuel
      if (typeof localStorage !== "undefined") {
        localStorage.setItem("restaurant-info", JSON.stringify(payload));
      }
      showToast("success", "Informations enregistrées");
      if (typeof onSaved === "function") {
        onSaved();
      }
    } catch (error) {
      console.error("Sauvegarde infos restaurant:", error);
      showToast("error", "Impossible d'enregistrer les informations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadInfo = async () => {
      try {
        const res = await fetch("/api/restaurant-info");
        if (res.ok) {
          const data = await res.json();
          setInitial(data);
          setLogoPath(String(data?.logo_path || ""));
          setMenuCovers(Array.isArray(data?.menu_covers) ? data.menu_covers.slice(0, 3) : []);
          setMenuDescriptions(
            Array.isArray(data?.menu_descriptions) ? data.menu_descriptions.slice(0, 4) : [],
          );
          setBusinessType(extractBusinessType(data));

          Object.entries(data).forEach(([key, value]) => {
            if (refs.current[key]) {
              if (typeof value === "string" || typeof value === "number") {
                refs.current[key].value = String(value);
              }
            }
          });
          if (typeof localStorage !== "undefined") {
            localStorage.setItem("restaurant-info", JSON.stringify(data));
          }
          return;
        }
      } catch (error) {
        console.warn("Lecture infos restaurant API:", error);
      }
      // Fallback localStorage
      if (typeof localStorage !== "undefined") {
        try {
          const saved = localStorage.getItem("restaurant-info");
          if (saved) {
            const parsed = JSON.parse(saved);
            setInitial(parsed);
            setLogoPath(String(parsed?.logo_path || ""));
            setMenuCovers(Array.isArray(parsed?.menu_covers) ? parsed.menu_covers.slice(0, 3) : []);
            setMenuDescriptions(
              Array.isArray(parsed?.menu_descriptions) ? parsed.menu_descriptions.slice(0, 4) : [],
            );
            setBusinessType(extractBusinessType(parsed));

            Object.entries(parsed).forEach(([key, value]) => {
              if (refs.current[key]) {
                if (typeof value === "string" || typeof value === "number") {
                  refs.current[key].value = String(value);
                }
              }
            });
          }
        } catch (error) {
          console.warn("Lecture infos restaurant local:", error);
        }
      }
    };
    loadInfo();
  }, []);

  const Input = ({ label, icon: Icon, field, type = "text", placeholder }) => (
    <label className="text-sm space-y-1 block">
      <span className={`flex items-center gap-2 ${isDarkMode ? "text-slate-200" : "text-slate-700"}`}>
        {Icon && <Icon size={14} />}
        {label}
      </span>
      <input
        ref={(node) => (refs.current[field] = node)}
        type={type}
        defaultValue={initial[field] || ""}
        placeholder={placeholder}
        className={`w-full rounded-2xl border px-3 py-2 text-sm ${
          isDarkMode
            ? "bg-white/10 border-white/10 text-white placeholder:text-white/50"
            : "bg-white border-slate-200 text-slate-900 placeholder:text-slate-400"
        }`}
      />
    </label>
  );

  const isBoutique = businessType === "boutique";

  return (
    <div className={keepMounted ? "" : "mt-6"}>
      <div className="flex items-center gap-3 mb-3">
        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${isDarkMode ? "bg-indigo-500/20 text-indigo-200" : "bg-indigo-50 text-indigo-700"}`}>
          Identité
        </span>
        <h4 className="text-lg font-semibold">Informations de l'établissement</h4>
      </div>
      <div className={`${cardClasses} rounded-2xl p-6 space-y-4`}>
        <div
          className={`rounded-2xl border p-4 ${
            isDarkMode
              ? "border-white/10 bg-white/5"
              : "border-slate-200 bg-slate-50"
          }`}
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className={`text-sm font-semibold ${isDarkMode ? "text-white" : "text-slate-900"}`}>
                Type d'établissement
              </div>
              <div className={`text-xs ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                Sélectionnez Restaurant ou Boutique pour adapter l'interface.
              </div>
            </div>
            <div
              className={`flex items-center gap-2 rounded-full border p-1 ${
                isDarkMode ? "border-white/10 bg-white/5" : "border-slate-200 bg-white"
              }`}
            >
              <button
                type="button"
                onClick={() => setBusinessType("restaurant")}
                className={`rounded-full px-4 py-1 text-xs font-semibold transition ${
                  businessType === "restaurant"
                    ? "bg-amber-400 text-slate-900"
                    : isDarkMode
                      ? "text-slate-300 hover:text-white"
                      : "text-slate-600 hover:text-slate-900"
                }`}
              >
                Restaurant
              </button>
              <button
                type="button"
                onClick={() => setBusinessType("boutique")}
                className={`rounded-full px-4 py-1 text-xs font-semibold transition ${
                  businessType === "boutique"
                    ? "bg-amber-400 text-slate-900"
                    : isDarkMode
                      ? "text-slate-300 hover:text-white"
                      : "text-slate-600 hover:text-slate-900"
                }`}
              >
                Boutique
              </button>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input label="Nom de l'établissement" icon={Building2} field="name" placeholder="Ex: Restaurant Djizef" />
          <Input label="Téléphone" icon={Phone} field="phone" placeholder="+225 XX XX XX XX" />
          <Input label="Adresse" icon={MapPin} field="address" placeholder="Rue, quartier, ville" />
          <Input label="Email" icon={AtSign} field="email" type="email" placeholder="contact@exemple.com" />
          <Input label="Site web" icon={Globe2} field="website" placeholder="https://example.com" />
          <Input label="Instagram" icon={Globe2} field="instagram" placeholder="@profil" />
          <Input label="Facebook" icon={Globe2} field="facebook" placeholder="facebook.com/..." />
          <Input label="TikTok" icon={Globe2} field="tiktok" placeholder="tiktok.com/@..." />
          <Input label="Nom du propriétaire" icon={User} field="owner_name" placeholder="Nom complet" />
          <Input label="Contact du propriétaire" icon={Phone} field="owner_phone" placeholder="+225 XX XX XX XX" />
        </div>

        <div className="mt-2 grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-white">Logo établissement</div>
                <div className="text-xs text-slate-400">Format recommandé: 120x120</div>
              </div>
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-2xl border border-white/15 px-3 py-2 text-xs font-semibold text-white hover:border-amber-400/50">
                {uploadingLogo ? "Upload..." : "Importer"}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  disabled={uploadingLogo}
                  onChange={(e) => handleUploadLogo(e.target.files?.[0] || null)}
                />
              </label>
            </div>

            <div className="mt-4 flex items-center gap-4">
              <div className="h-[120px] w-[120px] overflow-hidden rounded-full border border-white/10 bg-white/5">
                <img
                  src={logoPath || "/placeholder-menu.jpg"}
                  alt="Logo établissement"
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="text-xs text-slate-400">
                {logoPath ? (
                  <div className="break-all">Chemin: {logoPath}</div>
                ) : (
                  <div>Aucun logo défini.</div>
                )}
              </div>
            </div>
          </div>

          {!isBoutique && (
            <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-white">Covers du menu</div>
                  <div className="text-xs text-slate-400">Max 3 images (fade doux)</div>
                </div>
                <label className="inline-flex cursor-pointer items-center gap-2 rounded-2xl border border-white/15 px-3 py-2 text-xs font-semibold text-white hover:border-amber-400/50">
                  {uploadingCovers ? "Upload..." : "Ajouter"}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    disabled={uploadingCovers || (menuCovers?.length || 0) >= 3}
                    multiple
                    onChange={(e) => handleUploadCovers(e.target.files)}
                  />
                </label>
              </div>

              <div className="mt-4 grid grid-cols-3 gap-3">
                {(menuCovers || []).map((src, idx) => (
                  <div key={`${src}-${idx}`} className="group relative overflow-hidden rounded-xl border border-white/10 bg-white/5">
                    <img src={src} alt={`Cover ${idx + 1}`} className="h-20 w-full object-cover opacity-90" />
                    <button
                      type="button"
                      onClick={() => setMenuCovers((prev) => (prev || []).filter((_, i) => i !== idx))}
                      className="absolute right-1 top-1 rounded-lg border border-white/10 bg-black/50 px-2 py-1 text-[10px] font-semibold text-white opacity-0 transition-opacity group-hover:opacity-100"
                      title="Retirer"
                    >
                      Retirer
                    </button>
                  </div>
                ))}
                {Array.from({ length: Math.max(0, 3 - (menuCovers?.length || 0)) }).map((_, i) => (
                  <div key={`empty-${i}`} className="h-20 rounded-xl border border-dashed border-white/10 bg-white/5" />
                ))}
              </div>
            </div>
          )}
        </div>

        {!isBoutique && (
        <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-semibold text-white">Descriptions (header menu)</div>
              <div className="text-xs text-slate-400">Max 4. Rotation lente sur le menu public.</div>
            </div>
            <button
              type="button"
              onClick={() => {
                if ((menuDescriptions?.length || 0) >= 4) {
                  showToast("info", "Maximum 4 descriptions");
                  return;
                }
                setMenuDescriptions((prev) => [...(prev || []), ""]);
              }}
              className="inline-flex items-center gap-2 rounded-2xl border border-white/15 px-3 py-2 text-xs font-semibold text-white hover:border-amber-400/50"
            >
              + Ajouter description
            </button>
          </div>

          <div className="mt-4 space-y-2">
            {(menuDescriptions || []).length === 0 ? (
              <div className="text-sm text-slate-400">Aucune description.</div>
            ) : (
              (menuDescriptions || []).map((text, idx) => (
                <div key={`desc-${idx}`} className="flex items-center gap-2">
                  <input
                    value={text}
                    onChange={(e) =>
                      setMenuDescriptions((prev) =>
                        (prev || []).map((v, i) => (i === idx ? e.target.value : v)),
                      )
                    }
                    maxLength={120}
                    placeholder="Ex: Cuisine africaine moderne - Signature du chef..."
                    className={`w-full rounded-2xl border px-3 py-2 text-sm ${
                      isDarkMode
                        ? "bg-white/10 border-white/10 text-white placeholder:text-white/50"
                        : "bg-white border-slate-200 text-slate-900 placeholder:text-slate-400"
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setMenuDescriptions((prev) => (prev || []).filter((_, i) => i !== idx))
                    }
                    className="rounded-2xl border border-white/15 px-3 py-2 text-xs font-semibold text-white hover:border-red-400/50"
                    title="Supprimer"
                  >
                    Supprimer
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
        )}

        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl bg-gradient-to-r from-orange-500 to-amber-500 text-white font-semibold shadow hover:from-orange-600 hover:to-amber-600 disabled:opacity-60"
          >
            {loading ? "Enregistrement..." : "Enregistrer"}
          </button>
        </div>
      </div>
    </div>
  );
}
